import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, from, map, mergeMap, Observable, throwError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';
import { FileResponse } from './file-response';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorHandlerService);

  getFile(url: string) {
    return this.http.get<FileResponse>(url).pipe(catchError(() => throwError(() => 'Failed to load file')));
  }

  uploadFile(file: File, apiUrl: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);

    return this.http.post<FileResponse>(apiUrl, formData).pipe(catchError((err) => this.errorService.handle(err)));
  }

  uploadFileFromUrl(url: string, apiUrl: string, fileName: string = 'Default name') {
    return from(this.createFileFromURL(url, fileName)).pipe(
      mergeMap((file) => {
        if (file) {
          return this.uploadFile(file, apiUrl);
        }
        return EMPTY;
      }),
    );
  }

  getBlob(blobUrl: string): Observable<Blob> {
    return this.http.get(blobUrl, { responseType: 'blob' });
  }

  isBlobUrl(url: string): boolean {
    return url.startsWith('blob:');
  }

  blobToFile(blobUrl: string, filename: string): Observable<File> {
    return this.getBlob(blobUrl).pipe(
      map(blob => new File([blob], filename, { type: blob.type || 'application/octet-stream' }))
    );
  }

  downloadBlob(blobUrl: string, filename: string): Observable<void> {
    return this.getBlob(blobUrl).pipe(
      map(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
    );
  }

  removeFile(apiUrl: string, storyId: string) {
    return this.http.delete(`${apiUrl}/${storyId}`);
  }

  private async createFileFromURL(url: string, fileName: string) {
    try {
      // Fetch the data from the URL
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch file from URL: ${response.statusText}`);
      }

      // Convert the response into a Blob
      const blob = await response.blob();

      // Create a File object from the Blob
      const file = new File([blob], fileName, { type: blob.type });

      return file;
    } catch (error) {
      console.error('Error creating file:', error);
      return null;
    }
  }
}
